import { createSlice } from '@reduxjs/toolkit';
import {
  BENEFITS_JOB,
  CATEGORY_JOB,
  DETAILS_JOB,
  HOW_TO_APPLY_JOB,
  ID_JOB,
  INTERNAL_CANDIDATES_ENABLED_JOB,
  REGIONAL_RESTRICTIONS_JOB,
  STATUS_ACTIVE,
  STATUS_INACTIVE,
  STATUS_JOB,
  TITLE_JOB,
  TYPE_JOB,
  USER_ID_JOB
} from '../../../common/adapters';
import {
  BASE_END_POINT_COMPANY,
  BASE_END_POINT_JOB,
  BETTER_PLAN_BILL,
  IS_VALID_DRAFT,
  JOB_END_POINT,
  PLAN_BILL
} from '../../../config';
import {
  clearDraftJobSession,
  getDraftJobSession,
  saveDraftJobSession
} from '../../../lib/sessionStorage';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import { loginSucceeded, logoutRequested } from '../user/usersSlice';
import { trackJobCreated } from '../../../helpers';

export const draftInitialJob = {};
draftInitialJob[ID_JOB] = null;
draftInitialJob[TITLE_JOB] = '';
draftInitialJob[CATEGORY_JOB] = 'dev';
draftInitialJob[REGIONAL_RESTRICTIONS_JOB] = '🌏 Worldwide';
draftInitialJob[BENEFITS_JOB] = '';
draftInitialJob[HOW_TO_APPLY_JOB] = '';
draftInitialJob[TYPE_JOB] = 'full_time';
draftInitialJob[DETAILS_JOB] = null;
draftInitialJob[IS_VALID_DRAFT] = false;
draftInitialJob[PLAN_BILL] = BETTER_PLAN_BILL;
draftInitialJob[STATUS_JOB] = STATUS_INACTIVE; // Default to inactive - requires payment to activate
draftInitialJob[INTERNAL_CANDIDATES_ENABLED_JOB] = true; // Default to ON - use internal candidates platform

const initialState = {
  current: null,
  currentEnriched: null, // Current JobResult with job, company, and file
  currentEnrichedLoading: false,
  currentEnrichedError: null,
  list: [],
  loading: false, // General loading state for job creation
  error: null, // General error state
  enrichedList: [], // List of JobResult objects with job, company, and file
  enrichedLoading: false,
  enrichedError: null,
  lastFetchedFilters: null, // Track which filters were used for the last fetch
  // Pagination state
  currentPage: 0,
  hasMore: true,
  totalJobs: 0,
  isLoadingMore: false,
  loadCount: 0, // Track number of loads
  maxLoadsReached: false, // Track if max loads reached (45)
  // Sticky jobs state
  stickyJobs: [], // Separate list for sticky jobs
  stickyJobsLoading: false,
  stickyJobsFetched: false,
  stickyJobsError: null,
  draft: draftInitialJob,
  userList: [], // List of user's jobs (just the job objects)
  userEnrichedList: [], // List of user's enriched job results (job, company, file)
  userListLoading: false, // Loading state for user jobs
  userListError: null, // Error state for user jobs
  userListFetched: false, // Track if user jobs have been fetched
  paymentRequiredJob: null, // Job that requires payment for activation
  paymentDialogOpen: false, // Payment dialog open state (managed in Redux)
  jobWasCreatedMoveToPlans: false, // Navigation flag to move to plan package after job creation
  updatingJobId: null, // Currently updating job ID (for loading states)
  deleteConfirmationJob: null, // Job pending deletion confirmation
  deleteDialogOpen: false, // Delete confirmation dialog open state
  // Company jobs count cache
  companyJobsCounts: {}, // { companyId: count }
  companyJobsCountsLoading: {}, // { companyId: boolean }
  refreshUserJobs: false,
  filter: {
    textSearch: '',
    categories: '',
    types: ''
  }
};

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    getJobByIdSucceeded(state, action) {
      state.current = action.payload.job;
    },
    getJobsByCurrentUserSucceeded(state, action) {
      state.userList = action.payload.jobs;
    },
    getJobsSucceeded(state, action) {
      state.list = action.payload.jobs;
    },
    fetchEnrichedJobsRequest(state, action) {
      state.enrichedLoading = true;
      state.enrichedError = null;
      state.lastFetchedFilters = action.payload.filters;
      // Reset pagination when starting a new fetch (not a "load more")
      if (!action.payload.isLoadMore) {
        state.currentPage = 0;
        state.loadCount = 0;
        state.maxLoadsReached = false;
      }
    },
    fetchEnrichedJobsSuccess(state, action) {
      state.enrichedLoading = false;
      state.enrichedList = action.payload.jobResults;
      state.enrichedError = null;
      // Update pagination metadata if provided
      if (action.payload.pagination) {
        state.currentPage = action.payload.pagination.currentPage;
        state.hasMore = action.payload.pagination.hasMore;
        state.totalJobs = action.payload.pagination.totalElements;
      }
    },
    appendEnrichedJobsSuccess(state, action) {
      state.isLoadingMore = false;
      // Append new jobs to existing list
      state.enrichedList = [
        ...state.enrichedList,
        ...action.payload.jobResults
      ];
      state.enrichedError = null;
      // Update pagination metadata
      if (action.payload.pagination) {
        state.currentPage = action.payload.pagination.currentPage;
        state.hasMore = action.payload.pagination.hasMore;
        state.totalJobs = action.payload.pagination.totalElements;
      }
      // Increment load count
      state.loadCount += 1;
      // Check if max loads reached (45)
      if (state.loadCount >= 45) {
        state.maxLoadsReached = true;
        state.hasMore = false;
      }
    },
    fetchEnrichedJobsFailure(state, action) {
      state.enrichedLoading = false;
      state.isLoadingMore = false;
      state.enrichedError = action.payload.error;
    },
    startLoadingMore(state) {
      state.isLoadingMore = true;
    },
    fetchStickyJobsRequest(state) {
      state.stickyJobsLoading = true;
    },
    fetchStickyJobsSuccess(state, action) {
      state.stickyJobsLoading = false;
      state.stickyJobs = action.payload.jobResults;
      state.stickyJobsFetched = true;
    },
    fetchStickyJobsFailure(state, action) {
      state.stickyJobsLoading = false;
      state.stickyJobsFetched = true; // Mark as fetched to prevent infinite retries
      state.stickyJobsError =
        action.payload?.error || 'Failed to fetch sticky jobs';
    },
    getEnrichedJobsSucceeded(state, action) {
      state.enrichedList = action.payload.jobResults;
    },
    fetchEnrichedJobByIdRequest(state) {
      state.currentEnrichedLoading = true;
      state.currentEnrichedError = null;
    },
    fetchEnrichedJobByIdSuccess(state, action) {
      state.currentEnrichedLoading = false;
      state.currentEnriched = action.payload.jobResult;
      state.current = action.payload.jobResult.job; // Also update current for backward compatibility
      state.currentEnrichedError = null;
    },
    fetchEnrichedJobByIdFailure(state, action) {
      state.currentEnrichedLoading = false;
      state.currentEnrichedError = action.payload.error;
    },
    draftJobUpdated(state, action) {
      saveDraftJobSession(action.payload.job);
      state.draft = action.payload.job;
    },
    draftJobSessionUpdated(state, action) {
      saveDraftJobSession(action.payload.job);
    },
    draftJobCacheCleared(state) {
      clearDraftJobSession();
      state.draft = null;
    },
    setCurrentJob(state, action) {
      state.current = action.payload.job;
    },
    doRefreshUserJobs(state) {
      state.refreshUserJobs = true;
    },
    resetRefreshUserJobs(state) {
      state.refreshUserJobs = false;
    },
    createJobSucceeded(state, action) {
      state.draft = action.payload.job;
    },
    createJobRequest(state) {
      state.loading = true;
      state.error = null;
    },
    createJobSuccess(state, action) {
      state.loading = false;
      state.draft = action.payload.job;
      state.current = action.payload.job;
      state.error = null;
    },
    createJobFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    },
    updateJobRequest(state) {
      state.loading = true;
      state.error = null;
    },
    updateJobSuccess(state, action) {
      state.loading = false;
      state.current = action.payload.job;
      state.error = null;
    },
    updateJobFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    },
    setJobWasCreatedMoveToPlans(state, action) {
      state.jobWasCreatedMoveToPlans = action.payload;
    },
    resetJobWasCreatedMoveToPlans(state) {
      state.jobWasCreatedMoveToPlans = false;
    },
    filterJobUpdated(state, action) {
      state.filter = action.payload.filter;
    },
    filterCategoriesUpdated(state, action) {
      state.filter.categories = action.payload.categories;
    },
    filterTypesUpdated(state, action) {
      state.filter.types = action.payload.types;
    },
    clearFilters(state) {
      state.filter.categories = '';
      state.filter.types = '';
      state.filter.textSearch = '';
      // Reset pagination when clearing filters
      state.currentPage = 0;
      state.loadCount = 0;
      state.maxLoadsReached = false;
      state.hasMore = true;
      state.lastFetchedFilters = null;
    },
    isValidDraftJobUpdated(state, action) {
      state.draft[IS_VALID_DRAFT] = action.payload.isValid;
    },
    updateJobInUserList(state, action) {
      const updatedJob = action.payload.job;
      const index = state.userList.findIndex((job) => job.id === updatedJob.id);
      console.log('🔄 [Redux] updateJobInUserList called');
      console.log('📋 [Redux] Updated job:', updatedJob);
      console.log('📋 [Redux] Found at index:', index);
      if (index !== -1) {
        console.log('📋 [Redux] Old job:', state.userList[index]);
        // Create a new array with the updated job to ensure React detects the change
        state.userList = [
          ...state.userList.slice(0, index),
          updatedJob,
          ...state.userList.slice(index + 1)
        ];
        console.log('📋 [Redux] New job:', state.userList[index]);
        console.log(
          '✅ [Redux] Job list updated, new length:',
          state.userList.length
        );
      } else {
        console.error('❌ [Redux] Job not found in userList!');
      }
    },
    removeJobFromUserList(state, action) {
      const jobId = action.payload.jobId;
      state.userList = state.userList.filter((job) => job.id !== jobId);
    },
    clearUserJobs(state) {
      state.userList = [];
      state.userEnrichedList = [];
      state.current = null;
      state.userListFetched = false;
    },
    jobActivationPaymentRequired(state, action) {
      // Store the job that requires payment and open dialog
      state.paymentRequiredJob = action.payload.job;
      state.paymentDialogOpen = true;
    },
    closePaymentDialog(state) {
      // Close dialog and clear payment required job
      state.paymentDialogOpen = false;
      state.paymentRequiredJob = null;
    },
    openDeleteConfirmationDialog(state, action) {
      // Store the job that user wants to delete and open dialog
      state.deleteConfirmationJob = action.payload.job;
      state.deleteDialogOpen = true;
    },
    closeDeleteConfirmationDialog(state) {
      // Close dialog and clear delete confirmation job
      state.deleteDialogOpen = false;
      state.deleteConfirmationJob = null;
    },
    setUpdatingJobId(state, action) {
      state.updatingJobId = action.payload.jobId;
    },
    clearUpdatingJobId(state) {
      state.updatingJobId = null;
    },
    // User jobs thunk reducers
    fetchUserJobsRequest(state) {
      state.userListLoading = true;
      state.userListError = null;
    },
    fetchUserJobsSuccess(state, action) {
      state.userListLoading = false;
      state.userList = action.payload.jobs;
      state.userEnrichedList = action.payload.jobResults; // Store enriched data
      state.userListError = null;
      state.userListFetched = true;
    },
    fetchUserJobsFailure(state, action) {
      state.userListLoading = false;
      state.userListError = action.payload.error;
      state.userListFetched = true;
    },
    // Company jobs count thunk reducers
    fetchCompanyJobsCountRequest(state, action) {
      const companyId = action.payload.companyId;
      state.companyJobsCountsLoading[companyId] = true;
    },
    fetchCompanyJobsCountSuccess(state, action) {
      const { companyId, count } = action.payload;
      state.companyJobsCountsLoading[companyId] = false;
      state.companyJobsCounts[companyId] = count;
    },
    fetchCompanyJobsCountFailure(state, action) {
      const companyId = action.payload.companyId;
      state.companyJobsCountsLoading[companyId] = false;
    }
  },
  extraReducers: (builder) => {
    // Clear user jobs when login succeeds to ensure fresh data fetch
    builder.addCase(loginSucceeded, (state) => {
      console.log(
        '🔄 [Jobs Redux] Login succeeded, clearing user jobs to fetch fresh data'
      );
      state.userList = [];
      state.current = null;
      state.userListFetched = false;
      state.userListLoading = false;
      state.userListError = null;
    });

    // Clear user jobs when logout is requested
    builder.addCase(logoutRequested, (state) => {
      console.log('🔄 [Jobs Redux] Logout requested, clearing all job data');
      state.userList = [];
      state.current = null;
      state.userListFetched = false;
      state.userListLoading = false;
      state.userListError = null;
    });
  }
});

export const currentJobSelector = (state) => state.jobs.current;
export const currentEnrichedJobSelector = (state) => state.jobs.currentEnriched;
export const currentEnrichedJobLoadingSelector = (state) =>
  state.jobs.currentEnrichedLoading;
export const currentEnrichedJobErrorSelector = (state) =>
  state.jobs.currentEnrichedError;
export const jobCreatingSelector = (state) => state.jobs.loading;
export const jobCreatingErrorSelector = (state) => state.jobs.error;
export const jobWasCreatedMoveToPlansSelector = (state) =>
  state.jobs.jobWasCreatedMoveToPlans;
export const howToApplyCurrentJobSelector = (state) =>
  currentJobSelector(state)[HOW_TO_APPLY_JOB];
export const jobListSelector = (state) => state.jobs.list;
export const enrichedJobListSelector = (state) => {
  // Filter out any job results that have null or invalid data
  return (state.jobs.enrichedList || []).filter((jobResult) => {
    // Ensure we have valid job data
    if (!jobResult || !jobResult.job || !jobResult.job.id) {
      console.warn(
        '⚠️ [enrichedJobListSelector] Skipping invalid job result:',
        jobResult
      );
      return false;
    }
    return true;
  });
};
export const enrichedJobLoadingSelector = (state) => state.jobs.enrichedLoading;
export const enrichedJobErrorSelector = (state) => state.jobs.enrichedError;
export const enrichedJobPaginationSelector = (state) => ({
  currentPage: state.jobs.currentPage,
  hasMore: state.jobs.hasMore,
  totalJobs: state.jobs.totalJobs,
  isLoadingMore: state.jobs.isLoadingMore,
  loadCount: state.jobs.loadCount,
  maxLoadsReached: state.jobs.maxLoadsReached
});
export const stickyJobListSelector = (state) => state.jobs.stickyJobs || [];
export const stickyJobsLoadingSelector = (state) =>
  state.jobs.stickyJobsLoading;
export const stickyJobsFetchedSelector = (state) =>
  state.jobs.stickyJobsFetched;
export const lastFetchedFiltersSelector = (state) =>
  state.jobs.lastFetchedFilters;
export const userJobListSelector = (state) => state.jobs.userList;
export const refreshUserJobsSelector = (state) => state.jobs.refreshUserJobs;
export const userEnrichedJobListSelector = (state) =>
  state.jobs.userEnrichedList;
export const userJobListLoadingSelector = (state) => state.jobs.userListLoading;
export const userJobListErrorSelector = (state) => state.jobs.userListError;
export const userJobListFetchedSelector = (state) => state.jobs.userListFetched;
export const paymentRequiredJobSelector = (state) =>
  state.jobs.paymentRequiredJob;
export const paymentDialogOpenSelector = (state) =>
  state.jobs.paymentDialogOpen;
export const deleteConfirmationJobSelector = (state) =>
  state.jobs.deleteConfirmationJob;
export const deleteDialogOpenSelector = (state) => state.jobs.deleteDialogOpen;
export const updatingJobIdSelector = (state) => state.jobs.updatingJobId;
export const companyJobsCountSelector = (companyId) => (state) =>
  state.jobs.companyJobsCounts[companyId] || 0;
export const companyJobsCountLoadingSelector = (companyId) => (state) =>
  state.jobs.companyJobsCountsLoading[companyId] || false;
export const draftJobSelector = (state) =>
  getDraftJobSession() ? getDraftJobSession() : state.jobs.draft;
export const isValidDraftJobSelector = (state) =>
  state.jobs.draft[IS_VALID_DRAFT];
export const jobFilterSelector = (state) => state.jobs.filter;
export const jobFilterCategoriesSelector = (state) =>
  state.jobs.filter.categories;
export const jobFilterTypesSelector = (state) => state.jobs.filter.types;

export const {
  getJobByIdSucceeded,
  getJobsByCurrentUserSucceeded,
  getJobsSucceeded,
  getEnrichedJobsSucceeded,
  fetchEnrichedJobsRequest,
  fetchEnrichedJobsSuccess,
  appendEnrichedJobsSuccess,
  fetchEnrichedJobsFailure,
  startLoadingMore,
  fetchStickyJobsRequest,
  fetchStickyJobsSuccess,
  fetchStickyJobsFailure,
  fetchEnrichedJobByIdRequest,
  fetchEnrichedJobByIdSuccess,
  fetchEnrichedJobByIdFailure,
  draftJobUpdated,
  createJobSucceeded,
  createJobRequest,
  createJobSuccess,
  createJobFailure,
  updateJobRequest,
  updateJobSuccess,
  updateJobFailure,
  setJobWasCreatedMoveToPlans,
  resetJobWasCreatedMoveToPlans,
  filterJobUpdated,
  filterCategoriesUpdated,
  filterTypesUpdated,
  clearFilters,
  draftJobCacheCleared,
  draftJobSessionUpdated,
  isValidDraftJobUpdated,
  updateJobInUserList,
  removeJobFromUserList,
  clearUserJobs,
  setCurrentJob,
  doRefreshUserJobs,
  resetRefreshUserJobs,
  jobActivationPaymentRequired,
  closePaymentDialog,
  openDeleteConfirmationDialog,
  closeDeleteConfirmationDialog,
  setUpdatingJobId,
  clearUpdatingJobId,
  fetchUserJobsRequest,
  fetchUserJobsSuccess,
  fetchUserJobsFailure,
  fetchCompanyJobsCountRequest,
  fetchCompanyJobsCountSuccess,
  fetchCompanyJobsCountFailure
} = jobsSlice.actions;

// Thunk action to fetch enriched jobs with pagination support
export const fetchEnrichedJobs =
  ({ category, type, page = 0, size = 10, isLoadMore = false }) =>
  async (dispatch, getState) => {
    const state = getState();
    const lastFetchedFilters = state.jobs.lastFetchedFilters;
    const currentFilters = { category, type };
    const isLoading = state.jobs.enrichedLoading;
    const isLoadingMore = state.jobs.isLoadingMore;
    const maxLoadsReached = state.jobs.maxLoadsReached;

    // Skip if currently loading
    if (isLoading || isLoadingMore) {
      console.log('🔄 [Jobs Thunk] Already loading - skipping');
      return;
    }

    // Check if max loads reached
    if (maxLoadsReached && isLoadMore) {
      console.log('🛑 [Jobs Thunk] Max loads (45) reached - skipping');
      return;
    }

    // For initial load (not load more), check if we already fetched with these exact filters
    if (
      !isLoadMore &&
      lastFetchedFilters &&
      lastFetchedFilters.category === currentFilters.category &&
      lastFetchedFilters.type === currentFilters.type &&
      page === 0
    ) {
      console.log(
        '🔄 [Jobs Thunk] Skipping fetch - already fetched with these filters'
      );
      return;
    }

    console.log(
      '🔍 [Jobs Thunk] Starting fetch with filters:',
      currentFilters,
      'page:',
      page,
      'size:',
      size
    );
    console.log('🌐 [Jobs Thunk] Gateway URL:', BASE_END_POINT_COMPANY);

    if (isLoadMore) {
      dispatch(startLoadingMore());
    } else {
      dispatch(
        fetchEnrichedJobsRequest({ filters: currentFilters, isLoadMore: false })
      );
    }

    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (type) params.append('type', type);
      params.append('page', page);
      params.append('size', size);

      const queryString = params.toString();
      const url = `${BASE_END_POINT_COMPANY}/api/v1/job/active?${queryString}`;

      console.log('🌐 [Jobs Thunk] Fetching from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Log raw response text before parsing to help debug JSON errors
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        console.error(
          '❌ [Jobs Thunk] Failed to parse JSON response from /active'
        );
        console.error(
          '📄 [Jobs Thunk] Response text (first 500 chars):',
          responseText.substring(0, 500)
        );
        console.error(
          '📄 [Jobs Thunk] Response text (last 500 chars):',
          responseText.substring(Math.max(0, responseText.length - 500))
        );
        throw new Error(`Invalid JSON response: ${jsonError.message}`);
      }

      const jobResults = data?.data || [];
      const pagination = data?.pagination;

      console.log(
        '✅ [Jobs Thunk] Received',
        jobResults.length,
        'enriched jobs, hasMore:',
        pagination?.hasMore
      );

      // Log data validation warnings
      jobResults.forEach((result, index) => {
        if (!result.job) {
          console.warn(
            `⚠️ [Jobs Thunk] Job at index ${index} is missing job data`
          );
        }
        if (!result.company) {
          console.warn(
            `⚠️ [Jobs Thunk] Job at index ${index} is missing company data`
          );
        }
      });

      if (isLoadMore) {
        dispatch(appendEnrichedJobsSuccess({ jobResults, pagination }));
      } else {
        dispatch(fetchEnrichedJobsSuccess({ jobResults, pagination }));
      }
    } catch (error) {
      console.error('❌ [Jobs Thunk] Failed to fetch enriched jobs:', error);
      dispatch(fetchEnrichedJobsFailure({ error: error.message }));
    }
  };

// Thunk action to fetch sticky jobs (jobs with active sticky plans)
export const fetchStickyJobs = () => async (dispatch, getState) => {
  const state = getState();

  // Skip if already fetched or currently loading
  if (state.jobs.stickyJobsFetched || state.jobs.stickyJobsLoading) {
    console.log('🔄 [Sticky Jobs Thunk] Already fetched or loading - skipping');
    return;
  }

  console.log('🔍 [Sticky Jobs Thunk] Fetching sticky jobs');
  console.log('🌐 [Sticky Jobs Thunk] Gateway URL:', BASE_END_POINT_COMPANY);

  dispatch(fetchStickyJobsRequest());

  try {
    const url = `${BASE_END_POINT_COMPANY}/api/v1/job/sticky`;
    console.log('🌐 [Sticky Jobs Thunk] Fetching from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Log raw response text before parsing to help debug JSON errors
    const responseText = await response.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (jsonError) {
      console.error(
        '❌ [Sticky Jobs Thunk] Failed to parse JSON response from /sticky'
      );
      console.error(
        '📄 [Sticky Jobs Thunk] Response text (first 500 chars):',
        responseText.substring(0, 500)
      );
      console.error(
        '📄 [Sticky Jobs Thunk] Response text (last 500 chars):',
        responseText.substring(Math.max(0, responseText.length - 500))
      );
      throw new Error(`Invalid JSON response: ${jsonError.message}`);
    }

    const jobResults = data?.data || [];

    console.log(
      '✅ [Sticky Jobs Thunk] Received',
      jobResults.length,
      'sticky jobs'
    );

    // Log data validation warnings
    jobResults.forEach((result, index) => {
      if (!result.job) {
        console.warn(
          `⚠️ [Sticky Jobs Thunk] Job at index ${index} is missing job data`
        );
      }
      if (!result.company) {
        console.warn(
          `⚠️ [Sticky Jobs Thunk] Job at index ${index} is missing company data`
        );
      }
    });

    dispatch(fetchStickyJobsSuccess({ jobResults }));
  } catch (error) {
    console.error('❌ [Sticky Jobs Thunk] Failed to fetch sticky jobs:', error);
    dispatch(fetchStickyJobsFailure({ error: error.message }));
  }
};

// Thunk action to fetch enriched job by ID (includes job, company, and file)
export const fetchEnrichedJobById =
  ({ jobId }) =>
  async (dispatch, getState) => {
    const state = getState();
    const isLoading = state.jobs.currentEnrichedLoading;
    const currentEnriched = state.jobs.currentEnriched;

    // Skip if currently loading
    if (isLoading) {
      console.log('🔄 [Enriched Job By ID Thunk] Already loading - skipping');
      return;
    }

    // Check if we already have this job in enrichedList
    const jobFromList = state.jobs.enrichedList.find(
      (jobResult) => jobResult.job && jobResult.job.id === jobId
    );

    if (jobFromList) {
      console.log(
        '✅ [Enriched Job By ID Thunk] Job found in enrichedList, using cached data'
      );
      dispatch(fetchEnrichedJobByIdSuccess({ jobResult: jobFromList }));
      return;
    }

    // Skip if we already have this exact job loaded
    if (
      currentEnriched &&
      currentEnriched.job &&
      currentEnriched.job.id === jobId
    ) {
      console.log(
        '🔄 [Enriched Job By ID Thunk] Skipping fetch - already have this job'
      );
      return;
    }

    console.log('🔍 [Enriched Job By ID Thunk] Fetching job:', jobId);

    dispatch(fetchEnrichedJobByIdRequest());

    try {
      // Use the orchestrator gateway to get enriched job result
      const url = `${BASE_END_POINT_COMPANY}/api/v1/job/${jobId}`;

      console.log('🌐 [Enriched Job By ID Thunk] Fetching from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const jobResult = data?.data;

      if (!jobResult || !jobResult.job || !jobResult.job.id) {
        throw new Error('Invalid job data received');
      }

      console.log('✅ [Enriched Job By ID Thunk] Job fetched successfully');

      dispatch(fetchEnrichedJobByIdSuccess({ jobResult }));
    } catch (error) {
      console.error(
        '❌ [Enriched Job By ID Thunk] Failed to fetch job:',
        error
      );
      dispatch(fetchEnrichedJobByIdFailure({ error: error.message }));
    }
  };

// Thunk action to activate job after successful payment
export const activateJobAfterPayment =
  ({ jobId, userId }) =>
  async (dispatch, getState) => {
    console.log('🎉 [Activate Job After Payment] Starting job activation');
    console.log('📋 [Activate Job After Payment] Job ID:', jobId);
    console.log('👤 [Activate Job After Payment] User ID:', userId);

    if (!jobId) {
      console.error('❌ [Activate Job After Payment] No job ID provided');
      return;
    }

    try {
      const state = getState();
      const userEnrichedJobs = state.jobs.userEnrichedList;

      // Find the job in the user's enriched job list
      const targetJobData = userEnrichedJobs.find(
        (jobResult) => jobResult.job && jobResult.job.id === jobId
      );

      const jobToActivate = targetJobData?.job;

      if (!jobToActivate) {
        console.warn(
          '⚠️ [Activate Job After Payment] Job not found in user jobs list, will fetch and activate'
        );

        // Fetch the job by ID
        const localAuth = getLocalStorageAuthentication();
        const url = `${BASE_END_POINT_JOB}${JOB_END_POINT}/${jobId}`;

        console.log('🌐 [Activate Job After Payment] Fetching job from:', url);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localAuth.token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job: ${response.status}`);
        }

        const data = await response.json();
        const jobResult = data?.data;
        const fetchedJob = jobResult?.job || jobResult;

        if (!fetchedJob || !fetchedJob.id) {
          throw new Error('Invalid job data received');
        }

        console.log('📦 [Activate Job After Payment] Fetched job:', fetchedJob);

        // Activate the fetched job
        const activatedJob = {
          ...fetchedJob,
          status: STATUS_ACTIVE,
          userId: userId
        };

        console.log('🔄 [Activate Job After Payment] Activating fetched job');

        dispatch(setUpdatingJobId({ jobId: activatedJob.id }));

        const updatedJob = dispatch(updateUserJob({ job: activatedJob }));

        dispatch(clearUpdatingJobId());

        if (updatedJob) {
          console.log(
            '✅ [Activate Job After Payment] Job activated successfully'
          );
          dispatch(
            messageUpdated({
              text: '🎉 Payment successful! Your job is now active and visible to candidates.',
              severity: Severity.SUCCESS
            })
          );

          // Refresh the job list
          dispatch(clearUserJobs());
          setTimeout(() => {
            dispatch(fetchUserJobs({ userId }));
          }, 500);

          return updatedJob;
        }
      } else {
        // Job found in the list, activate it directly
        console.log(
          '📦 [Activate Job After Payment] Job found:',
          jobToActivate
        );

        if (jobToActivate.status === STATUS_ACTIVE) {
          console.log('✅ [Activate Job After Payment] Job is already active');
          dispatch(
            messageUpdated({
              text: '✅ Payment successful! Your job is already active.',
              severity: Severity.SUCCESS
            })
          );

          // Refresh the job list anyway to ensure data is fresh
          dispatch(clearUserJobs());
          setTimeout(() => {
            dispatch(fetchUserJobs({ userId }));
          }, 500);

          return jobToActivate;
        }

        const activatedJob = {
          ...jobToActivate,
          status: STATUS_ACTIVE,
          userId: userId
        };

        console.log('🔄 [Activate Job After Payment] Activating job');

        dispatch(setUpdatingJobId({ jobId: activatedJob.id }));

        const updatedJob = dispatch(updateUserJob({ job: activatedJob }));

        dispatch(clearUpdatingJobId());

        if (updatedJob) {
          console.log(
            '✅ [Activate Job After Payment] Job activated successfully'
          );
          dispatch(
            messageUpdated({
              text: '🎉 Payment successful! Your job is now active and visible to candidates.',
              severity: Severity.SUCCESS
            })
          );

          // Refresh the job list
          dispatch(clearUserJobs());
          setTimeout(() => {
            dispatch(fetchUserJobs({ userId }));
          }, 500);

          return updatedJob;
        }
      }
    } catch (error) {
      console.error(
        '❌ [Activate Job After Payment] Failed to activate job:',
        error
      );
      dispatch(
        messageUpdated({
          text: 'Payment successful, but job activation failed. Please try activating manually from the dashboard.',
          severity: Severity.WARNING
        })
      );
      throw error;
    }
  };

// Thunk action to fetch user jobs
export const fetchUserJobs =
  ({ userId }) =>
  async (dispatch, getState) => {
    const state = getState();
    const isLoading = state.jobs.userListLoading;
    const userListFetched = state.jobs.userListFetched;

    // Skip if currently loading
    if (isLoading) {
      console.log('🔄 [User Jobs Thunk] Already loading - skipping');
      return;
    }

    // Skip if already fetched (even if empty)
    if (userListFetched) {
      console.log(
        '🔄 [User Jobs Thunk] Skipping fetch - already fetched user jobs'
      );
      return;
    }

    console.log('🔍 [User Jobs Thunk] Fetching user jobs for userId:', userId);

    dispatch(fetchUserJobsRequest());

    try {
      const localAuth = getLocalStorageAuthentication();
      const userIdEncoded = encodeURIComponent(userId);
      const url = `${BASE_END_POINT_JOB}${JOB_END_POINT}/user/${userIdEncoded}`;

      console.log('🌐 [User Jobs Thunk] Fetching from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const jobResults = data?.data || [];

      // Extract just the jobs from the JobResult objects
      const jobs = jobResults
        .map((jobResult) => jobResult.job)
        .filter((job) => job && job.id);

      console.log(
        '✅ [User Jobs Thunk] Received',
        jobs.length,
        'user jobs from',
        jobResults.length,
        'job results'
      );

      dispatch(fetchUserJobsSuccess({ jobs, jobResults }));
    } catch (error) {
      console.error('❌ [User Jobs Thunk] Failed to fetch user jobs:', error);
      dispatch(fetchUserJobsFailure({ error: error.message }));
    }
  };

// Thunk action to update a job
export const updateUserJob =
  ({ job }) =>
  async (dispatch) => {
    console.log('🔍 [Update Job Thunk] Updating job:', job.id);

    try {
      const localAuth = getLocalStorageAuthentication();
      saveDraftJobSession(job);

      const url = `${BASE_END_POINT_JOB}${JOB_END_POINT}/${job.id}`;

      console.log('🌐 [Update Job Thunk] Updating at:', url);
      console.log(
        '🔑 [Update Job Thunk] Using JWT token:',
        localAuth.token ? 'Present' : 'Missing'
      );
      console.log('👤 [Update Job Thunk] User ID:', localAuth.userId);
      console.log('📝 [Update Job Thunk] Job userId:', job.userId);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(job)
      });

      if (!response.ok) {
        // Handle 402 Payment Required error
        if (response.status === 402) {
          console.log(
            '💳 [Update Job Thunk] Payment required for job activation'
          );

          // Dispatch payment required action using slice.actions
          dispatch(jobsSlice.actions.jobActivationPaymentRequired({ job }));

          // Don't throw error, just return null to indicate payment required
          return null;
        }

        // Enhanced error logging for 403
        if (response.status === 403) {
          console.error(
            '❌ [Update Job Thunk] 403 Forbidden - Authorization failed'
          );
          console.error(
            '📋 [Update Job Thunk] Check if user owns the job and has proper permissions'
          );
        }

        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage += ` - ${errorData.message}`;
          }
        } catch (e) {
          // Ignore JSON parsing errors
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('📦 [Update Job Thunk] Response data:', data);

      // Handle both response formats:
      // 1. JobResult format: { data: { job, company, file } }
      // 2. Direct job format: { data: { ...job } }
      const jobResult = data?.data;
      const updatedJob = jobResult?.job || jobResult;

      if (!updatedJob || !updatedJob.id) {
        throw new Error('Invalid response: Updated job data not found');
      }

      console.log('✅ [Update Job Thunk] Job updated successfully');
      console.log(
        '📋 [Update Job Thunk] Updated job status:',
        updatedJob.status
      );
      console.log('📋 [Update Job Thunk] Updated job object:', updatedJob);

      // Update the job in the user list
      dispatch(updateJobInUserList({ job: updatedJob }));

      // Show success message
      dispatch(
        messageUpdated({
          text: 'Job updated successfully',
          severity: Severity.SUCCESS
        })
      );

      return updatedJob;
    } catch (error) {
      console.error('❌ [Update Job Thunk] Failed to update job:', error);

      // Show error message with more context for 403 errors
      const errorText = error.message.includes('403')
        ? 'Authorization failed. You may not have permission to update this job.'
        : 'Job not updated, please try again';

      dispatch(
        messageUpdated({
          text: errorText,
          severity: Severity.ERROR
        })
      );

      throw error;
    }
  };

// Thunk action to delete a job
export const deleteUserJob =
  ({ jobId }) =>
  async (dispatch) => {
    console.log('🔍 [Delete Job Thunk] Deleting job:', jobId);

    try {
      const localAuth = getLocalStorageAuthentication();

      const url = `${BASE_END_POINT_JOB}${JOB_END_POINT}/${jobId}`;

      console.log('🌐 [Delete Job Thunk] Deleting at:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('✅ [Delete Job Thunk] Job deleted successfully');

      // Remove the job from the user list
      dispatch(removeJobFromUserList({ jobId }));

      // Show success message
      dispatch(
        messageUpdated({
          text: 'Job deleted successfully',
          severity: Severity.SUCCESS
        })
      );

      return { jobId };
    } catch (error) {
      console.error('❌ [Delete Job Thunk] Failed to delete job:', error);

      // Show error message
      dispatch(
        messageUpdated({
          text: 'Job not deleted, please try again',
          severity: Severity.ERROR
        })
      );

      throw error;
    }
  };

// Thunk to create job via REST API
export const createJobThunk =
  ({ job }) =>
  async (dispatch, getState) => {
    console.log('🔄 [Job Thunk] Creating job:', job);

    dispatch(createJobRequest());

    try {
      const localAuth = getLocalStorageAuthentication();

      if (!localAuth || !localAuth.token) {
        throw new Error('Not authenticated');
      }

      // Remove ID for new jobs to prevent accidental updates
      const newJob = { ...job };
      delete newJob.id;

      // Set the userId to the currently authenticated user
      newJob[USER_ID_JOB] = localAuth.userId;

      // Set default status to 'inactive' for new jobs (requires payment to activate)
      if (!newJob.status) {
        newJob.status = STATUS_INACTIVE;
      }

      // Note: stripePriceId should be set from PlanPackage.js when user selects a tier/bundle
      // The job object from the form should already contain the stripePriceId from tier selection

      // Save to session storage
      saveDraftJobSession(newJob);

      const url = `${BASE_END_POINT_JOB}${JOB_END_POINT}`;

      console.log('🌐 [Job Thunk] POST:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(newJob)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create job: ${errorText}`);
      }

      const data = await response.json();
      const jobResult = data?.data;
      const createdJob = jobResult?.job;

      if (!createdJob || !createdJob.id) {
        throw new Error('Job created but no job ID returned');
      }

      console.log('✅ [Job Thunk] Job created successfully:', createdJob.id);

      // Save created job to session storage
      saveDraftJobSession(createdJob);

      // Dispatch success actions
      dispatch(createJobSuccess({ job: createdJob }));
      dispatch(createJobSucceeded(createdJob));

      // Track job creation in Google Analytics
      trackJobCreated(createdJob.id, createdJob.title || 'Unknown', {
        job_category: createdJob.category || 'Not specified',
        job_type: createdJob.type || 'Not specified',
        has_benefits: !!(createdJob.benefits && createdJob.benefits.length > 0),
        has_regional_restrictions: !!createdJob.regionalRestrictions,
        has_how_to_apply: !!createdJob.howToApply,
        company_id: createdJob.companyId || 'Not specified'
      });

      // Clear user jobs so dashboard will refetch
      dispatch(doRefreshUserJobs());
      // Show success message
      dispatch(
        messageUpdated({
          text: 'Job created successfully',
          severity: Severity.SUCCESS
        })
      );
      // Set navigation flag to move to plan package
      dispatch(setJobWasCreatedMoveToPlans(true));
      // Return the created job for use in component
      return createdJob;
    } catch (error) {
      console.error('❌ [Job Thunk] Failed to create job:', error);

      dispatch(createJobFailure({ error: error.message }));

      dispatch(
        messageUpdated({
          text: 'Job not created, please try again',
          severity: Severity.ERROR
        })
      );

      throw error; // Re-throw so component can handle
    }
  };

// Thunk to update job via REST API
export const updateJobThunk =
  ({ job }) =>
  async (dispatch, getState) => {
    console.log('🔄 [Job Thunk] Updating job:', job.id);

    dispatch(updateJobRequest());

    try {
      const localAuth = getLocalStorageAuthentication();

      if (!localAuth || !localAuth.token) {
        throw new Error('Not authenticated');
      }

      // Ensure the userId matches the authenticated user
      const updatedJob = { ...job };
      updatedJob[USER_ID_JOB] = localAuth.userId;

      // Save to session storage
      saveDraftJobSession(updatedJob);

      const url = `${BASE_END_POINT_JOB}${JOB_END_POINT}/${updatedJob.id}`;

      console.log('🌐 [Job Thunk] PUT:', url);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(updatedJob)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update job: ${errorText}`);
      }

      const data = await response.json();
      const jobResult = data?.data;
      const updatedJobFromApi = jobResult?.job;

      if (!updatedJobFromApi || !updatedJobFromApi.id) {
        throw new Error('Job updated but no job returned');
      }

      console.log(
        '✅ [Job Thunk] Job updated successfully:',
        updatedJobFromApi.id
      );

      // Dispatch success actions
      dispatch(updateJobSuccess({ job: updatedJobFromApi }));

      // Clear user jobs so dashboard will refetch
      dispatch(clearUserJobs());

      // Show success message
      dispatch(
        messageUpdated({
          text: 'Job updated successfully',
          severity: Severity.SUCCESS
        })
      );

      // Return the updated job for use in component
      return updatedJobFromApi;
    } catch (error) {
      console.error('❌ [Job Thunk] Failed to update job:', error);

      dispatch(updateJobFailure({ error: error.message }));

      dispatch(
        messageUpdated({
          text: 'Job not updated, please try again',
          severity: Severity.ERROR
        })
      );

      throw error; // Re-throw so component can handle
    }
  };

// Thunk action to fetch company jobs count
export const fetchCompanyJobsCount =
  ({ companyId }) =>
  async (dispatch, getState) => {
    // Check if already loading or cached
    const state = getState();
    const isLoading = state.jobs.companyJobsCountsLoading[companyId];
    const cachedCount = state.jobs.companyJobsCounts[companyId];

    if (isLoading) {
      console.log(
        '🔄 [Company Jobs Count Thunk] Already loading for company:',
        companyId
      );
      return;
    }

    if (cachedCount !== undefined) {
      console.log(
        '✅ [Company Jobs Count Thunk] Using cached count for company:',
        companyId,
        'Count:',
        cachedCount
      );
      return cachedCount;
    }

    console.log(
      '🔍 [Company Jobs Count Thunk] Fetching jobs count for company:',
      companyId
    );

    dispatch(fetchCompanyJobsCountRequest({ companyId }));

    try {
      const response = await fetch(
        `${BASE_END_POINT_JOB}/api/v1/job/active?page=0&size=50`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Filter jobs by company ID
      const companyJobs =
        result.data?.filter(
          (jobResult) => jobResult.company?.id === companyId
        ) || [];

      const count = companyJobs.length;
      console.log('✅ [Company Jobs Count Thunk] Found jobs count:', count);

      dispatch(fetchCompanyJobsCountSuccess({ companyId, count }));

      return count;
    } catch (error) {
      console.error(
        '❌ [Company Jobs Count Thunk] Failed to fetch jobs count:',
        error
      );
      dispatch(fetchCompanyJobsCountFailure({ companyId }));
      return 0;
    }
  };

export default jobsSlice.reducer;
